
import { AdminState } from '@/types/adminTypes';
import { createDefaultState, UPDATE_DELAY } from './adminStateConfig';
import { getStateFromCache, updateStateCache, getCachedStateOrDefault, clearStateCache } from './adminStateCache';
import { fetchStateFromDb, createDefaultStateInDb, updateStateInDb, setupBeforeUnloadHandler } from './adminStateDb';
import { pruneActiveAdmins } from './adminActiveService';

// Keep track of updates to batch them
let pendingStateUpdates: Partial<AdminState> = {};
let updateTimer: NodeJS.Timeout | null = null;

// Add debounce tracker
let isFetchingFromDb = false;

/**
 * Load admin state with caching and debouncing
 */
export const loadAdminState = async (forceRefresh = false): Promise<AdminState> => {
  try {
    // Use cached state if available and not forced refresh
    if (!forceRefresh) {
      const cachedState = getStateFromCache();
      if (cachedState) {
        return cachedState;
      }
    }
    
    // Prevent multiple simultaneous database queries
    if (isFetchingFromDb) {
      // Return cached state while fetch is in progress
      return getCachedStateOrDefault();
    }
    
    isFetchingFromDb = true;
    
    try {
      // Fetch from database
      const dbState = await fetchStateFromDb();
      
      if (dbState) {
        // Update cache and return state
        updateStateCache(dbState);
        return dbState;
      }
      
      // If no state exists in DB, create default state
      const defaultState = createDefaultState();
      const newState = await createDefaultStateInDb(defaultState);
      
      // Update cache and return new state
      updateStateCache(newState);
      return newState;
    } finally {
      // Always clear the fetching flag
      isFetchingFromDb = false;
    }
  } catch (error) {
    // Clear the fetching flag on error
    isFetchingFromDb = false;
    
    // Return cached state on error if available
    const cachedState = getCachedStateOrDefault();
    return cachedState;
  }
};

/**
 * Update admin state with improved batching to reduce database writes
 */
export const updateAdminState = async (updates: Partial<AdminState>): Promise<AdminState> => {
  try {
    // Get current state from cache when possible
    const currentState = getCachedStateOrDefault();
    
    // Merge updates into pending updates
    pendingStateUpdates = {
      ...pendingStateUpdates,
      ...updates,
      // For activeAdmins, we need special handling to avoid overwrites
      ...(updates.activeAdmins ? { activeAdmins: updates.activeAdmins } : {})
    };
    
    // Create a local updated state
    const updatedState: AdminState = {
      ...currentState,
      ...pendingStateUpdates,
      // If updating activeAdmins, maintain maximum size limit
      activeAdmins: pendingStateUpdates.activeAdmins 
        ? pruneActiveAdmins(pendingStateUpdates.activeAdmins)
        : pruneActiveAdmins(currentState.activeAdmins)
    };
    
    // Update local cache immediately
    updateStateCache(updatedState);
    
    // Implement debounced update with better cleanup
    if (updateTimer) {
      clearTimeout(updateTimer);
    }
    
    updateTimer = setTimeout(async () => {
      const finalUpdates = { ...pendingStateUpdates };
      pendingStateUpdates = {}; // Clear pending updates
      
      // Update in database
      await updateStateInDb(finalUpdates, currentState);
    }, UPDATE_DELAY);
    
    return updatedState;
  } catch (error) {
    // Return current state on error
    return getCachedStateOrDefault();
  }
};

// Set up event handler for page unload with optimized handlers
setupBeforeUnloadHandler(
  () => updateTimer,
  (timer) => {
    if (timer) clearTimeout(timer);
  },
  () => pendingStateUpdates,
  () => { pendingStateUpdates = {}; },
  getCachedStateOrDefault
);

// Re-export functions from adminActiveService
export { addActiveAdmin, removeActiveAdmin } from './adminActiveService';
